import React, { useCallback, useEffect, useRef} from "react";
import { Button, Comment, Tooltip } from 'antd';
import { map } from "lodash";
import ModalUtil from 'utils/modalUtil';
import { CellRenderers, Sorters } from 'utils/tableUtils';
import { DeleteIcon, EditIcon } from 'components/Icons';
import { useActiveUserContext, withActiveUser } from "contexts/ActiveUserContext";
import ToolConfigurationNotesPopup from "./ToolConfigurationNotesPopup";
import { ToolConfigurationNotesAPI } from 'restAPI/ToolsIntegrationAPI';
import DeletePopup from "./DeletePopup";
import ConfirmPopup from "./ConfirmPopup";

const NotesComponent = ({ notesList, getItemDataProps, setNotesList, toolConfigId, activeNoteId = undefined, users = []}) => {
  const activeUser = useActiveUserContext();
  const elementRefs = useRef({});

  useEffect(() => {
    if (activeNoteId && elementRefs.current[activeNoteId]) {
      elementRefs.current[activeNoteId].scrollIntoView({ behavior: 'smooth' });
    }
  }, [notesList, activeNoteId]);

  const onNoteDataChange = useCallback((noteId, newNoteContent = undefined, readStatus = undefined) => {
    // Updated note fields only for state change.
    let updatedNotes = notesList;
    if(newNoteContent !== undefined){
      updatedNotes = notesList.map(note => note.id === noteId ? { ...note, content : newNoteContent, updatedBy : activeUser.email, updatedAt : Date.now()} : note);
    }
    else if(readStatus !== undefined){
      updatedNotes = notesList.map(note => note.id === noteId ? { ...note, isRead : readStatus} : note);
    }
    setNotesList([...updatedNotes]);
  }, [notesList, activeUser, setNotesList]);

  const onNoteEdit = useCallback((note) => {
    ModalUtil.show({
      content: <ToolConfigurationNotesPopup
            okText={"Edit"}
            title={"Edit Note"}
            type={null}
            defaultValue={note.content}
            note
            noteLength={1000}
            users = {users}
            onOk={async (data) => {
              await ToolConfigurationNotesAPI.updateToolConfigurationNote(note.toolConfigId, note.id, data).promise;
              onNoteDataChange(note.id, data.htmlText, undefined);
            }}
        />
    });
  }, [onNoteDataChange, users]);

  const deleteNote = useCallback(
      (noteId) => {
          const updatedNotes = notesList.filter((note) => note.id !== noteId);
          setNotesList([...updatedNotes]);
      },
      [notesList, setNotesList]
  );

  const onNoteDelete = useCallback(
      async (note) => {
          ModalUtil.show({
              content: (
                  <DeletePopup
                      promptMessage={'Delete Note'}
                      note={false}
                      confirmText={'Delete'}
                      onDelete={async () => {
                          await ToolConfigurationNotesAPI.deleteToolConfigurationNote(
                              note.toolConfigId,
                              note.id
                          ).promise;
                          deleteNote(note.id);
                      }}
                  />
              ),
          });
      },
      [deleteNote]
  );

  const updateNoteReadStatus = useCallback(async (noteId, readStatus) => {
    ModalUtil.show({
        content: <ConfirmPopup
            title={`Are you sure you want to mark this note as unread?`}
            okText="Yes"
            onOk={async () => {
              const { promise } = ToolConfigurationNotesAPI.setToolConfigurationNoteReadStatus(toolConfigId, noteId, readStatus);
              await promise;
              onNoteDataChange(noteId, undefined, readStatus);
            }}
        />,
    });
  }, [onNoteDataChange, toolConfigId]);

  const renderNoteItem = useCallback((note) => {
    const itemDataProps = getItemDataProps(note);
    itemDataProps.datetime = (
      <>
        {itemDataProps.datetime}
        <>
          <Button type='link' size="small" onClick={() => onNoteEdit(note)}><EditIcon /></Button>
          <Button type='link' size="small" onClick={() => onNoteDelete(note)}><DeleteIcon /></Button>
          {
            (note.createdAt !== note.updatedAt) && (
                <Tooltip title={<>(modified by {note.updatedBy}<span> {CellRenderers.TIME(note.updatedAt)}</span>)</>} placement="top" overlayStyle={{ maxWidth: '500px' }}>
                <small>(edited)</small>
                </Tooltip>
            )
          }
          {
            (note.readStatusEnabled && <Button size='small' className='pull-right' type="link" onClick={async() => await updateNoteReadStatus(note.id, !note.isRead)}>Mark as {note.isRead ? "unread" : "read"}</Button>)
          }
        </>
      </>
    );
    
    return (
      <Comment {...itemDataProps} />
    );
  }, [getItemDataProps, onNoteEdit, onNoteDelete, updateNoteReadStatus]);

  return (
    <>
      {map(notesList.sort(Sorters.DATE('createdAt', true)), (note, idx) => {
        const noteElement = renderNoteItem(note);
        return <div key={`item-${idx}`} ref={el => (elementRefs.current[note.id] = el)}>{noteElement}</div>;
      })}
    </>
  );
};

export default withActiveUser(NotesComponent);
